/**
 * Whether a conversation's recap is shown. Two per-DEVICE settings, read together.
 *
 * - `symphony.recap.visible` — the MASTER switch, the one Settings owns. Off means no recap anywhere.
 * - `symphony.recap.hidden` — the ids of individual conversations whose recap the user closed.
 *
 * The banner's × and the chat header's menu item are per-conversation: a recap belongs to one thread, and
 * closing the line on a thread you have read says nothing about the next one. The master switch stays for
 * the user who does not want the feature at all.
 *
 * Turning one back ON clears the master too (`setRecapVisibleFor`). Otherwise "Show recap" would be a
 * control that does nothing whenever the master happens to be off, which is worse than not offering it.
 *
 * Stored per device rather than on the account deliberately — it is a display choice about one screen, and
 * syncing it would mean a write to the server every time someone closes a banner.
 *
 * Default ON for both: a recap only exists once a conversation has had enough turns to be worth
 * summarising, so the common case is that showing it is useful. Absent keys ⇒ on, so nobody is migrated.
 */
const STORAGE_KEY = 'symphony.recap.visible';
const HIDDEN_KEY = 'symphony.recap.hidden';

export function isRecapVisible(): boolean {
  // SSR and a blocked/absent localStorage both fall back to the default rather than throwing — this is
  // read during render.
  try {
    if (typeof window === 'undefined') {
      return true;
    }
    return window.localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function setRecapVisible(visible: boolean): void {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, visible ? 'true' : 'false');
    notify();
  } catch {
    // A device that cannot persist the choice still honours it for the session (React state), which is
    // better than failing the click.
  }
}

export function hiddenRecapConversationIds(): string[] {
  try {
    if (typeof window === 'undefined') {
      return [];
    }
    const raw = window.localStorage.getItem(HIDDEN_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    // A hand-edited or half-written value must not take the recap down for every conversation, so anything
    // that is not an array of strings reads as "nothing hidden".
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

export function isRecapVisibleFor(conversationId: string): boolean {
  if (!isRecapVisible()) {
    return false;
  }
  return !hiddenRecapConversationIds().includes(conversationId);
}

export function setRecapVisibleFor(conversationId: string, visible: boolean): void {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    const hidden = new Set(hiddenRecapConversationIds());
    if (visible) {
      hidden.delete(conversationId);
      // Showing has to actually show. See the master-switch note above.
      window.localStorage.setItem(STORAGE_KEY, 'true');
    } else {
      hidden.add(conversationId);
    }
    window.localStorage.setItem(HIDDEN_KEY, JSON.stringify([...hidden]));
    notify();
  } catch {
    // Same as `setRecapVisible`: a device that cannot persist still honours the click for this session.
  }
}

function notify(): void {
  // Same-tab listeners: the native `storage` event only fires in OTHER tabs, so a banner closed here would
  // keep rendering in this one until a remount without this.
  window.dispatchEvent(new CustomEvent(RECAP_VISIBILITY_EVENT));
}

export const RECAP_VISIBILITY_EVENT = 'symphony:recap-visibility';
